import React, { useState, useEffect } from 'react';
import { Modal, Table, Spinner, Alert, Button, Pagination } from 'react-bootstrap';
import { api } from '../services/api';
import { formatDateRu, formatDateTimeRu } from '../utils/dateFormat';
import { getActionTypeLabel } from './ActionTypesModal';

function formatValue(val: any): string {
  if (val == null) return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  const s = String(val);
  return s.length > 80 ? s.slice(0, 80) + '…' : s;
}

interface ActionTypeHistoryModalProps {
  show: boolean;
  onHide: () => void;
  actionType: string;
  actionTypeLabel: string;
  dateFrom: string;
  dateTo: string;
}

const ActionTypeHistoryModal: React.FC<ActionTypeHistoryModalProps> = ({
  show,
  onHide,
  actionType,
  actionTypeLabel,
  dateFrom,
  dateTo
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const limit = 20;

  useEffect(() => {
    if (!show || !actionType || !dateFrom || !dateTo) return;
    setLoading(true);
    setError(null);
    api.getStatisticsAuditLog(actionType, dateFrom, dateTo, page, limit)
      .then((res) => {
        setData(res.data);
        setTotal(res.total);
        setTotalPages(res.totalPages);
      })
      .catch((e: any) => {
        setError(e?.response?.data?.error || e?.message || 'Ошибка загрузки');
        setData([]);
      })
      .finally(() => setLoading(false));
  }, [show, actionType, dateFrom, dateTo, page]);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          История по типу: {actionTypeLabel}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Период: {formatDateRu(dateFrom)} — {formatDateRu(dateTo)}. Ваши действия.
        </p>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {!loading && data.length === 0 && !error && (
          <p className="text-muted mb-0">Нет записей за выбранный период.</p>
        )}
        {!loading && data.length > 0 && (
          <>
            <Table responsive size="sm" variant="dark" hover>
              <thead>
                <tr>
                  <th>Дата и время</th>
                  <th>Товар (ID / артикул)</th>
                  <th>Было</th>
                  <th>Стало</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTimeRu(row.created_at)}</td>
                    <td>
                      {row.product_id != null
                        ? (row.product_article ? `${row.product_id} / ${row.product_article}` : String(row.product_id))
                        : '—'}
                    </td>
                    <td className="text-break" style={{ maxWidth: 200 }}>{formatValue(row.old_value)}</td>
                    <td className="text-break" style={{ maxWidth: 200 }}>{formatValue(row.new_value)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-2">
                <span className="small text-muted">Всего: {total}</span>
                <Pagination size="sm" className="mb-0">
                  <Pagination.Prev disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
                  <Pagination.Item active>{page} / {totalPages}</Pagination.Item>
                  <Pagination.Next disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
                </Pagination>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Закрыть</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ActionTypeHistoryModal;
